import React from "react";
import { createTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import Langs from '../../app/lang/langs';

// Icons come from @mui/icons-material, which this project already depends on. There is no
// Material Icons webfont loaded in index.html, so a `<span className="material-icons">`
// would render the ligature name as literal text — hence components, and hence
// VLEARN_TYPE_META carrying a component reference rather than an icon name.
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import LinkIcon from '@mui/icons-material/Link';
import QuizIcon from '@mui/icons-material/Quiz';
import SchoolIcon from '@mui/icons-material/School';

/**
 * V-Learning — the shared visual language.
 *
 * Every V-Learning screen imports from here rather than restyling the same card, chip and
 * progress bar four times. That is the whole purpose: the module is six screens built by
 * different hands, and without one source for the surface treatment they drift into six
 * slightly different products.
 *
 * Built with MUI `sx` and the application's existing CSS custom properties (`--orange`,
 * `--green`, `--lightGrey`, `--text-primary`…, defined in `App.css`), so V-Learning inherits
 * the platform's palette instead of introducing a second one. No new CSS framework, no new
 * dependency.
 */

// ── The MUI theme ────────────────────────────────────────────────────────────────────

/** The brand accent, matching `--orange` in App.css. */
export const VLEARN_ORANGE = '#FF9015'

/**
 * A scoped MUI theme so every control in V-Learning picks up the Vital orange instead of
 * MUI's default blue.
 *
 * **There is no global MUI theme in this application**, so `palette.primary` is MUI's own
 * `#1976d2` everywhere unless a component overrides it by hand. That is why a focused text
 * field, a checked switch, a selected radio, a slider and a DataGrid checkbox all rendered
 * blue in the settings screen — nothing was wrong with those components, they were simply
 * inheriting a palette nobody had set.
 *
 * Fixing it here rather than with an `sx` prop per control is the whole point: there are
 * dozens of MUI controls across the module's screens, hand-colouring each one is a list that
 * is never finished, and the next control anyone adds would arrive blue again. One
 * `ThemeProvider` at `VLearnView` covers every screen, every dialog and every portal —
 * `Select` menus, `Autocomplete` poppers and `Tooltip`s render through a React portal but
 * still inherit context, so they are covered too.
 *
 * Follows the precedent already set by `containers/leaves/LeaveAdjustmentsModal.js`, which
 * does exactly this for the same reason.
 *
 * `error` and `success` are set alongside it so a validation message and a success chip agree
 * with `--red` / `--green` rather than being the only two colours still coming from MUI.
 *
 * NOTE what this deliberately does NOT change: the categorical colours in
 * `VLEARN_TYPE_META` (a lesson type) and `ActivityFeed`'s action table. Those blues are
 * carrying meaning — they are how eight lesson types and nine activity actions stay
 * distinguishable at a glance — and flattening them to orange would destroy the distinction
 * rather than fix a brand slip.
 */
export const vlearnTheme = createTheme({
    palette: {
        primary: { main: VLEARN_ORANGE, contrastText: '#ffffff' },
        error: { main: '#fb6962' },
        success: { main: '#3a9c60' }
    },
    // 8 rather than MUI's 4, so an input sits comfortably next to the module's 14px cards.
    shape: { borderRadius: 8 },
    typography: { fontFamily: 'roboto, sans-serif' },
    components: {
        // The palette alone leaves a focused outlined input with MUI's 2px ring in the new
        // colour but its resting border unchanged; nudging the hover state keeps the three
        // states reading as one control.
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 144, 21, 0.55)'
                    }
                }
            }
        },
        // MUI's default indicator is `primary`, which the palette now fixes — but several
        // V-Learning screens were written before this theme existed and set the orange by
        // hand. Both now agree, so those hand-overrides are redundant rather than wrong.
        MuiTab: {
            styleOverrides: {
                root: { textTransform: 'none' }
            }
        },
        MuiButton: {
            styleOverrides: {
                // Sentence case throughout, matching the rest of the module.
                root: { textTransform: 'none' }
            }
        }
    }
})

// ── Tokens ───────────────────────────────────────────────────────────────────────────

/**
 * The surface treatment, in one object.
 *
 * A card, a panel and a table container in this module differ only in padding — sharing the
 * radius, border and shadow is what makes them read as one system. `hover` is separated so a
 * static panel does not inherit an interaction affordance it does not have.
 */
export const vlearnSurface = {
    card: {
        borderRadius: '14px',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 14px rgba(0,0,0,0.04)',
        overflow: 'hidden'
    },
    hover: {
        transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.07)',
            borderColor: 'rgba(255, 144, 21, 0.35)'
        }
    },
    panel: {
        borderRadius: '14px',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        backgroundColor: '#fff',
        padding: '20px'
    },
    // Reduced motion is honoured for the hover lift rather than only for keyframe animations:
    // a translate on every card in a 40-card catalogue is exactly the kind of movement the
    // preference is about.
    reducedMotion: {
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': { transform: 'none' }
        }
    }
}

/** The module's type scale. Sizes are in rem so the app's own root sizing still governs. */
export const vlearnType = {
    pageTitle: { fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary, #111318)' },
    sectionTitle: { fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary, #111318)' },
    cardTitle: { fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary, #111318)', lineHeight: 1.35 },
    body: { fontSize: '0.85rem', color: 'var(--text-secondary, #4b5263)', lineHeight: 1.5 },
    meta: { fontSize: '0.75rem', color: 'var(--text-muted, #8b92a0)' }
}

/**
 * One colour per progress status, used by the chip, the ring and the bar alike.
 *
 * Grey / orange / green follows `getTagClass()` in app/utils/helpers.js, so a "completed"
 * pill here is the same green as a completed anything else in the platform.
 */
export const VLEARN_STATUS_STYLES = {
    not_started: { bg: 'var(--lightGrey, #ededed)', fg: '#3a3a3a', bar: '#c4c4c4', key: 'VLEARN_STATUS_NOT_STARTED' },
    in_progress: { bg: 'rgba(255, 144, 21, 0.14)', fg: '#B25E00', bar: 'var(--orange, rgb(255,144,21))', key: 'VLEARN_STATUS_IN_PROGRESS' },
    completed: { bg: 'rgba(58, 156, 96, 0.14)', fg: '#1F7A4C', bar: 'var(--green, rgb(58,156,96))', key: 'VLEARN_STATUS_COMPLETED' },
    overdue: { bg: 'rgba(251, 105, 98, 0.16)', fg: '#B3271F', bar: 'var(--red, rgb(251,105,98))', key: 'VLEARN_STATUS_OVERDUE' }
}

/**
 * The icon, accent and label key for each lesson type, so a curriculum row is scannable at a
 * glance and every screen labels a type identically.
 *
 * `Icon` is a component, not a name — see the import comment above.
 */
export const VLEARN_TYPE_META = {
    video_youtube: { Icon: SmartDisplayIcon, color: '#C4302B', key: 'VLEARN_TYPE_VIDEO_YOUTUBE' },
    video_upload: { Icon: PlayCircleOutlineIcon, color: '#1179be', key: 'VLEARN_TYPE_VIDEO_UPLOAD' },
    pdf: { Icon: PictureAsPdfIcon, color: '#C4302B', key: 'VLEARN_TYPE_PDF' },
    slides: { Icon: SlideshowIcon, color: '#D97706', key: 'VLEARN_TYPE_SLIDES' },
    document: { Icon: DescriptionIcon, color: '#1179be', key: 'VLEARN_TYPE_DOCUMENT' },
    text: { Icon: ArticleIcon, color: '#4b5263', key: 'VLEARN_TYPE_TEXT' },
    link: { Icon: LinkIcon, color: '#0067b8', key: 'VLEARN_TYPE_LINK' },
    quiz: { Icon: QuizIcon, color: '#8E4EC6', key: 'VLEARN_TYPE_QUIZ' }
}

/** The icon for a lesson type, with a sane fallback for a type this table does not know. */
export function VLearnTypeIcon({ type, fontSize = 'small', sx }) {
    const meta = VLEARN_TYPE_META[type] || VLEARN_TYPE_META.text
    const Icon = meta.Icon
    return <Icon fontSize={fontSize} sx={{ color: meta.color, ...sx }} />
}

// ── Primitives ───────────────────────────────────────────────────────────────────────

/**
 * The status pill.
 *
 * Takes a status key, never a colour: a caller that picks its own colour is a caller that
 * will disagree with the next one about what "in progress" looks like.
 */
export function VLearnStatusChip({ status, overdue, size = 'small' }) {
    const style = VLEARN_STATUS_STYLES[overdue ? 'overdue' : status] || VLEARN_STATUS_STYLES.not_started
    return (
        <Chip
            size={size}
            label={<Langs str={style.key} />}
            sx={{
                backgroundColor: style.bg,
                color: style.fg,
                fontWeight: 600,
                fontSize: '0.72rem',
                height: size === 'small' ? 22 : 28,
                borderRadius: '7px'
            }}
        />
    )
}

/**
 * A circular progress indicator drawn with two SVG circles.
 *
 * Inline SVG rather than MUI's `CircularProgress` with `variant="determinate"`, because the
 * percentage has to sit inside the ring and the track has to stay visible behind the arc —
 * neither of which that component offers without fighting it.
 *
 * The arc is drawn from `strokeDasharray`/`strokeDashoffset` on a circle rotated -90° so 0%
 * starts at twelve o'clock, which is the only orientation people read as "none of it done".
 */
export function VLearnProgressRing({ percent = 0, size = 44, thickness = 4, status }) {
    const value = Math.max(0, Math.min(100, Number(percent) || 0))
    const radius = (size - thickness) / 2
    const circumference = 2 * Math.PI * radius
    const style = VLEARN_STATUS_STYLES[status] || (
        value >= 100 ? VLEARN_STATUS_STYLES.completed
            : value > 0 ? VLEARN_STATUS_STYLES.in_progress
                : VLEARN_STATUS_STYLES.not_started
    )

    return (
        <Box
            sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
            role="img"
            aria-label={`${Math.round(value)}%`}
        >
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
                <circle
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth={thickness}
                />
                <circle
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none" stroke={style.bar} strokeWidth={thickness} strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference - (value / 100) * circumference}
                    style={{ transition: 'stroke-dashoffset .4s ease' }}
                />
            </svg>
            <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: size >= 44 ? '0.72rem' : '0.62rem', fontWeight: 700,
                color: 'var(--text-primary, #111318)'
            }}>
                {Math.round(value)}%
            </Box>
        </Box>
    )
}

/** The horizontal equivalent, for table rows and the course header. */
export function VLearnProgressBar({ percent = 0, status, height = 6 }) {
    const value = Math.max(0, Math.min(100, Number(percent) || 0))
    const style = VLEARN_STATUS_STYLES[status] || (
        value >= 100 ? VLEARN_STATUS_STYLES.completed
            : value > 0 ? VLEARN_STATUS_STYLES.in_progress
                : VLEARN_STATUS_STYLES.not_started
    )
    return (
        <LinearProgress
            variant="determinate"
            value={value}
            sx={{
                height, borderRadius: height / 2, backgroundColor: 'rgba(0,0,0,0.07)',
                '& .MuiLinearProgress-bar': { backgroundColor: style.bar, borderRadius: height / 2 }
            }}
        />
    )
}

/**
 * A KPI tile for the teacher dashboard.
 *
 * `hint` is deliberately a separate slot from `value`: a rate is only meaningful next to its
 * denominator, and "68%" with "17 of 25 learners" underneath is a different statement from
 * "68%" alone.
 */
export function VLearnStatTile({ label, value, hint, accent = 'var(--orange, rgb(255,144,21))', icon }) {
    return (
        <Box sx={{ ...vlearnSurface.card, padding: '16px 18px', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {/* `icon` is a rendered element from @mui/icons-material, e.g.
                    <GroupIcon fontSize="small" /> — the caller keeps control of its size. */}
                {icon ? (
                    <Box sx={{ display: 'flex', color: accent, '& svg': { fontSize: 18 } }} aria-hidden="true">
                        {icon}
                    </Box>
                ) : null}
                <Typography sx={{ ...vlearnType.meta, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600 }}>
                    {label}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.1, color: 'var(--text-primary, #111318)' }}>
                {value}
            </Typography>
            {hint ? <Typography sx={{ ...vlearnType.meta, mt: 0.25 }}>{hint}</Typography> : null}
        </Box>
    )
}

/**
 * The empty state.
 *
 * One component rather than an ad-hoc "Aucun résultat" in each screen, because an empty
 * state is where a user most needs to be told what to do next — `action` is the slot for
 * that, and having it in the shared primitive is what stops screens from shipping without one.
 */
export function VLearnEmptyState({ icon, title, description, action }) {
    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', padding: '48px 24px', gap: 1
        }}>
            <Box sx={{
                width: 60, height: 60, borderRadius: '50%',
                backgroundColor: 'rgba(255, 144, 21, 0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1
            }}>
                {/* `icon` is a rendered @mui/icons-material element; School is the module's
                    own default so a caller with nothing better to show still gets a shape. */}
                <Box sx={{
                    display: 'flex', color: 'var(--orange, rgb(255,144,21))',
                    '& svg': { fontSize: 30 }
                }} aria-hidden="true">
                    {icon || <SchoolIcon />}
                </Box>
            </Box>
            <Typography sx={{ ...vlearnType.sectionTitle }}>{title}</Typography>
            {description ? (
                <Typography sx={{ ...vlearnType.body, maxWidth: 420 }}>{description}</Typography>
            ) : null}
            {action ? <Box sx={{ mt: 1.5 }}>{action}</Box> : null}
        </Box>
    )
}

/**
 * The loading placeholder for a card grid.
 *
 * Skeletons rather than a spinner, and shaped like the cards they stand in for: the
 * catalogue's layout is known before its data is, so reserving it stops the page reflowing
 * under the reader when the response lands.
 */
export function VLearnCardSkeletons({ count = 6 }) {
    return (
        <>
            {Array.from({ length: count }).map((_, index) => (
                <Box key={index} sx={{ ...vlearnSurface.card }}>
                    <Skeleton variant="rectangular" height={124} />
                    <Box sx={{ padding: '14px 16px' }}>
                        <Skeleton width="70%" height={22} />
                        <Skeleton width="45%" height={16} />
                        <Skeleton width="100%" height={8} sx={{ mt: 1.5, borderRadius: 4 }} />
                    </Box>
                </Box>
            ))}
        </>
    )
}

/**
 * The responsive grid the catalogue and the dashboard tiles both sit on.
 *
 * The minimum track is wrapped in `min(<track>, 100%)`, and that is load-bearing on a phone.
 * A bare `minmax(268px, 1fr)` is a FLOOR, not a preference: when the container is narrower
 * than 268px the track stays 268px and the cards spill out of it, which is a horizontally
 * scrolling catalogue on a 360px screen. At 360px the catalogue's content box is roughly
 * 256px once the module padding and the card padding are taken off, so the overflow was real
 * rather than theoretical.
 *
 * `min(268px, 100%)` collapses the floor to the container on a narrow screen and leaves it at
 * 268px everywhere else, so the desktop layout is byte-for-byte unchanged.
 */
export const vlearnGrid = {
    cards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(268px, 100%), 1fr))',
        gap: { xs: '12px', sm: '18px' }
    },
    tiles: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
        gap: { xs: '10px', sm: '14px' }
    }
}

/**
 * The input sizing every V-Learning form field must carry.
 *
 * 16px on a phone is not a taste decision: iOS Safari ZOOMS THE WHOLE PAGE when a focused
 * input's computed font-size is below 16px, and it does not zoom back out afterwards. The
 * user is then stranded on a horizontally scrolling form. Every text field in this module
 * used to be set to 0.86rem (13.8px), which meant tapping any of them on an iPhone broke the
 * page layout for the rest of the session.
 *
 * The desktop size is unchanged — the override only applies below `sm`.
 */
export const vlearnInputSx = {
    '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '0.86rem' } }
}

/**
 * The minimum comfortable tap target, per the WCAG 2.2 target-size guidance.
 *
 * Spread onto an icon-only control, whose MUI `size="small"` default is 30px square — under a
 * fingertip that is a miss as often as a hit.
 */
export const vlearnTouchTarget = {
    minWidth: 44,
    minHeight: 44
}

/**
 * Seconds to a compact human duration ("4 min", "1 h 12").
 *
 * Lives here rather than in each screen because a lesson's length appears in the catalogue,
 * the sidebar, the player header and the dashboard, and four independent formatters is four
 * chances for them to disagree.
 */
export function formatVLearnDuration(seconds) {
    const total = Math.max(0, Math.round(Number(seconds) || 0))
    if (!total) return null
    if (total < 60) return `${total} s`
    const minutes = Math.round(total / 60)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest ? `${hours} h ${String(rest).padStart(2, '0')}` : `${hours} h`
}

/**
 * Whether an enrolment is past its due date.
 *
 * Compared on the date alone, not the timestamp: a course due "the 14th" is not late at
 * 09:00 on the 14th, and a naive `new Date(due) < new Date()` says it is.
 */
export function isVLearnOverdue(dueDate, status) {
    if (!dueDate || status === 'completed') return false
    const due = new Date(dueDate)
    if (Number.isNaN(due.getTime())) return false
    const today = new Date()
    due.setHours(23, 59, 59, 999)
    return due.getTime() < today.getTime()
}
